import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowDecision, FlowNote } from '../FlowPrimitives';

// ============================================================================
// Experiment 4 – EDTA Standardization & Water Hardness
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp4',
  title: 'EDTA Preparation, Standardization, and Analysis',
  objectives: [
    'Prepare approximately 0.01 M EDTA solution from Na₂H₂Y·2H₂O (disodium salt dihydrate)',
    'Standardize EDTA against a known Mg\u00B2⁺ solution',
    'Determine calcium content in water samples',
    'Understand chelation chemistry and 1:1 stoichiometry',
  ],
  safetyNotes: [
    'Wear safety glasses at all times while in the laboratory',
    'Use a rubber safety bulb when transferring solutions with a pipet – do not pipet by mouth',
    'Sulfuric acid is dangerous to the skin – wash immediately if spilled and inform the instructor',
    'Ammonia is a respiratory irritant and heart stimulant – confine pouring of ammonia buffer to exhaust hood',
  ],
  equipment: [
    'Analytical balance',
    'Weighing boat',
    '500 mL volumetric flask',
    '500 mL beaker',
    'Glass stir rod or magnetic stir bar',
    '25 mL pipet',
    'Buret',
    '250 mL Erlenmeyer flasks (3 for Part II, 3 for Part III)',
    'Rubber safety bulb',
  ],
  chemicals: [
    'Na₂H₂Y·2H₂O (dried EDTA disodium salt)',
    'One pellet of NaOH',
    '0.01 M Mg\u00B2⁺ standard solution (MgCl₂)',
    'pH 10 buffer (NH₃/NH₄Cl)',
    'Eriochrome Black T (EBT) indicator (or Calmagite)',
    'Mg/EDTA solution (for sharper colour change)',
    'Deionised water',
    'Unknown water sample',
  ],
  procedure: [
    {
      number: 1,
      title: 'Calculate EDTA Mass',
      details: [
        'Calculate mass for ~500 mL of 0.01 M solution',
        'Mass = 0.01 mol/L × 0.5 L × 372.24 g/mol \u2248 1.86 g',
      ],
      manualRef: 'Part I',
    },
    {
      number: 2,
      title: 'Prepare EDTA Solution',
      details: [
        'Weigh accurately 1.8–1.9 g Na₂H₂Y·2H₂O on analytical balance',
        'Transfer quantitatively to 500 mL beaker',
        'Add one pellet of NaOH and about 400 mL DI water',
        'Dissolve by stirring (gentle heating up to 40°C permitted)',
        'Transfer quantitatively to 500 mL volumetric flask and dilute to mark',
        'Mix thoroughly by inverting several times',
      ],
      manualRef: 'Part I',
    },
    {
      number: 3,
      title: 'Set Up Standardization',
      details: [
        'Pipette 3 × 25 mL aliquots of 0.01 M Mg\u00B2⁺ standard into 3 separate Erlenmeyer flasks',
        'Add 1 mL pH 10 buffer to each flask',
        'Add 2–4 drops EBT indicator – solution turns red',
      ],
      safetyNote: 'Ammonia buffer – confine pouring to exhaust hood',
      manualRef: 'Part II',
    },
    {
      number: 4,
      title: 'Titrate with EDTA',
      details: [
        'Fill buret with EDTA solution and record initial volume',
        'Titrate until colour changes from red to distinct blue',
        'Record final volume',
        'Repeat for all three flasks (triplicate)',
        'Perform blank correction: 25 mL DI water + 1 mL buffer + 2–4 drops EBT',
      ],
      manualRef: 'Part II',
    },
    {
      number: 5,
      title: 'Calculate EDTA Molarity',
      details: [
        'M(EDTA) = (Volume Mg\u00B2⁺ std (L) × Molarity Mg\u00B2⁺ std) / Titre EDTA (L)',
        'Subtract blank correction volume from each titre before calculating',
        'Average all three trials for the final EDTA molarity',
      ],
      manualRef: 'Part II',
    },
    {
      number: 6,
      title: 'Analyze Water Sample',
      details: [
        'Obtain unknown water sample and dilute 5-fold with DI water',
        'Pipette 25 mL of diluted sample into each of 3 Erlenmeyer flasks',
        'Add 2 mL pH 10 buffer, 1 mL Mg/EDTA solution, and 5–6 drops EBT to each flask (at exhaust hood)',
        'Titrate to distinct blue endpoint with standardized EDTA',
      ],
      manualRef: 'Part III',
    },
    {
      number: 7,
      title: 'Calculate Calcium ppm',
      details: [
        'moles EDTA = Titre EDTA (L) × Molarity EDTA',
        'moles EDTA = moles Ca\u00B2⁺ (1:1 stoichiometry)',
        'ppm Ca\u00B2⁺ = (moles Ca\u00B2⁺ × Molar Mass Ca\u00B2⁺ × 10⁶) / 25 g',
        'Account for 5-fold dilution in final result',
      ],
      manualRef: 'Part III',
    },
  ],
  keyFormulas: [
    'M\u00B2⁺ + H₂Y\u00B2⁻ → MY\u00B2⁻ + 2H⁺',
    'M(EDTA) = (Volume Mg\u00B2⁺ std (L) × Molarity Mg\u00B2⁺ std) / Titre EDTA (L)',
    'ppm Ca\u00B2⁺ = (moles Ca\u00B2⁺ × Molar Mass Ca\u00B2⁺ × 10⁶) / 25 g',
  ],
  tips: [
    'Ensure the solution is well-mixed before standardization – invert the flask at least 20 times',
    'Approach the endpoint slowly; the red → distinct blue transition is irreversible',
    'EBT indicator degrades over time – use fresh solution for best results',
    'Consistent pipetting technique is critical for triplicate precision',
    'Read the buret at the bottom of the meniscus to ±0.02 mL',
    'Part III uses more buffer (2 mL), Mg/EDTA (1 mL), and more EBT (5–6 drops) than Part II',
  ],
};

// ============================================================================
// Flowchart
// ============================================================================

function Flowchart() {
  // Layout constants
  const cx = 210; // center x for nodes (600/2 - 180/2 = 210)
  const nodeW = 180;
  const nodeH = 56;
  const termW = 140;
  const termH = 36;
  const decisionSize = 60;

  // Vertical positions
  const startY = 40;
  const n1Y = 90;
  const n2Y = 164;
  const n3Y = 238;
  const dY = 312;            // decision diamond
  const n4Y = 400;
  const n5Y = 474;
  const endY = 548;

  return (
    <FlowContainer width={600} height={600} title="EDTA Standardization Procedure Flow">
      {/* Start */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={startY} label="Start" />

      {/* Arrow: Start -> Prepare EDTA */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: startY + termH }}
        to={{ x: cx + nodeW / 2, y: n1Y }}
      />

      {/* Step 1: Prepare EDTA */}
      <FlowNode x={cx} y={n1Y} title="Prepare EDTA" subtitle="Weigh & dissolve in 500 mL" />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n1Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n2Y }}
      />

      {/* Step 2: Standardize vs Mg2+ */}
      <FlowNode x={cx} y={n2Y} title="Standardize vs Mg\u00B2⁺" subtitle="Titrate with EBT indicator" color="#2563eb" />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n2Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n3Y }}
      />

      {/* Step 3: Calculate Molarity */}
      <FlowNode x={cx} y={n3Y} title="Calculate Molarity" subtitle="M = (V_Mg × M_Mg) / V_EDTA" />

      {/* Arrow to Decision */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n3Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: dY }}
      />

      {/* Decision: Triplicates agree? */}
      <FlowDecision
        x={cx + nodeW / 2 - decisionSize / 2}
        y={dY}
        size={decisionSize}
        label="Agree?"
      />

      {/* No branch: loop back to Standardize */}
      <FlowArrow
        from={{ x: cx + nodeW / 2 + decisionSize / 2, y: dY + decisionSize / 2 }}
        to={{ x: cx + nodeW + 30, y: dY + decisionSize / 2 }}
        label="No"
        color="#ef4444"
      />
      <FlowArrow
        from={{ x: cx + nodeW + 30, y: dY + decisionSize / 2 }}
        to={{ x: cx + nodeW + 30, y: n2Y + nodeH / 2 }}
        color="#ef4444"
        dashed
      />
      <FlowArrow
        from={{ x: cx + nodeW + 30, y: n2Y + nodeH / 2 }}
        to={{ x: cx + nodeW, y: n2Y + nodeH / 2 }}
        label="Repeat"
        color="#ef4444"
      />

      {/* Yes branch: continue to Analyze Water */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: dY + decisionSize }}
        to={{ x: cx + nodeW / 2, y: n4Y }}
        label="Yes"
      />

      {/* Step 4: Analyze Water Sample */}
      <FlowNode x={cx} y={n4Y} title="Analyze Water Sample" subtitle="Titrate unknown sample" color="#7c3aed" />

      {/* Arrow */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n4Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: n5Y }}
      />

      {/* Step 5: Calculate Ca ppm */}
      <FlowNode x={cx} y={n5Y} title="Calculate Ca\u00B2⁺ ppm" subtitle="(moles × MM × 10⁶) / 25 g" />

      {/* Arrow to End */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: n5Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: endY }}
      />

      {/* End */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={endY} label="End" color="#ef4444" />

      {/* Safety note */}
      <FlowNote x={20} y={n2Y + 6} text="Use fume hood for buffer" width={140} />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp4Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
